import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Code, CheckCircle, ChevronLeft, Lock, Unlock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const DIFFICULTY_COLOURS = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700',
};

export default function InternshipDetails() {
  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [enrolled, setEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const loadProject = useCallback(async () => {
    try {
      const res = await api.get(`/internships/${id}`);
      setProject(res.data.project);
      setTasks(res.data.tasks);
    } catch {
      setError('Project not found.');
    }
  }, [id]);

  const checkEnrollment = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get(`/internships/${id}/enrolled`);
      setEnrolled(res.data.enrolled);
    } catch {}
  }, [id, isAuthenticated]);

  useEffect(() => {
    Promise.all([loadProject(), checkEnrollment()]).finally(() => setLoading(false));
  }, [loadProject, checkEnrollment]);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/internships/${id}` } });
      return;
    }

    setPaying(true);
    setError('');
    try {
      const orderRes = await api.post('/internships/payments/create-order', { project_id: id });
      const { order_id, amount, key_id, project_title } = orderRes.data;

      // Load Razorpay script dynamically
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency: 'INR',
        name: 'Eternal Shishya',
        description: project_title,
        order_id,
        handler: async function (response) {
          try {
            await api.post('/internships/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              project_id: id,
            });
            navigate(`/internships/${id}/workspace`);
          } catch {
            setError('Payment succeeded but enrollment failed. Please contact support.');
          }
        },
        prefill: {},
        theme: { color: '#F5A623' },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not initiate payment.');
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-4 border-saffron-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error && !project) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <p className="text-red-500">{error}</p>
      <Link to="/internships" className="text-saffron-600 hover:underline">← Back to Internships</Link>
    </div>
  );

  if (!project) return null;

  return (
    <div className="pt-16 min-h-screen bg-background">
      {/* Header */}
      <div className="bg-earth-900 py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <Link to="/internships" className="inline-flex items-center text-earth-300 hover:text-saffron-400 text-sm mb-6 gap-1 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back to Internships
          </Link>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${DIFFICULTY_COLOURS[project.difficulty_level]}`}>
              {project.difficulty_level}
            </span>
            <span className="bg-saffron-400 text-white px-3 py-1 rounded-full text-xs font-semibold">{project.category}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-earth-50 mb-3">{project.title}</h1>
          <p className="text-earth-300 text-lg mb-4 max-w-2xl">{project.short_description}</p>
          <div className="flex flex-wrap gap-5 text-sm text-earth-400">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-saffron-400" /> {project.duration_hours} hours</span>
            <span className="flex items-center gap-1"><Code className="w-4 h-4 text-saffron-400" /> {project.tech_stack}</span>
            <span className="flex items-center gap-1">👨‍🏫 {project.mentor_name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="md:col-span-2 space-y-8">
          {/* About */}
          <section className="bg-card border border-earth-200 rounded-2xl p-6">
            <h2 className="text-xl font-serif font-bold text-earth-900 mb-3">About This Project</h2>
            <p className="text-earth-600 leading-relaxed">{project.details || project.short_description}</p>
          </section>

          {/* Task list */}
          <section className="bg-card border border-earth-200 rounded-2xl p-6">
            <h2 className="text-xl font-serif font-bold text-earth-900 mb-4">
              What You'll Build — 5 Tasks × 3 Hours
            </h2>
            <div className="space-y-3">
              {tasks.map((task, i) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-earth-50 border border-earth-100"
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-saffron-400 text-white font-bold text-sm flex items-center justify-center">
                    {task.task_number}
                  </span>
                  <div>
                    <p className="font-semibold text-earth-900">{task.title}</p>
                    <p className="text-sm text-earth-500 mt-0.5">{task.description}</p>
                    <span className="inline-block mt-1 text-xs text-saffron-700 bg-saffron-50 px-2 py-0.5 rounded">
                      ~{task.expected_hours} hours
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* How it works */}
          <section className="bg-card border border-earth-200 rounded-2xl p-6">
            <h2 className="text-xl font-serif font-bold text-earth-900 mb-4">How It Works</h2>
            <ol className="space-y-3">
              {[
                ['Enroll & Pay', 'Secure one-time enrollment fee — quick and safe.'],
                ['Access Workspace', 'Get full access to all 5 task guides and step-by-step instructions.'],
                ['Complete Tasks', 'Work through each task. Mark it in-progress, follow the guide, build it.'],
                ['Pass the Quiz', 'Answer 5 MCQs per task (need 3/5 to pass). Up to 3 attempts.'],
                ['Get Certified', 'Complete all 5 tasks → auto-generate your Eternal Shishya completion certificate.'],
              ].map(([title, desc], i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-earth-900 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <div>
                    <span className="font-semibold text-earth-800">{title}: </span>
                    <span className="text-earth-500 text-sm">{desc}</span>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* Sticky sidebar */}
        <div className="md:col-span-1">
          <div className="sticky top-6 bg-card border border-earth-200 rounded-2xl p-6">
            <img
              src={project.image_url}
              alt={project.title}
              className="w-full h-40 object-cover rounded-xl mb-5"
              onError={e => { e.target.src = 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800'; }}
            />
            <p className="text-3xl font-bold text-saffron-600 mb-1">
              ₹{Number(project.enrollment_fee).toLocaleString('en-IN')}
            </p>
            <p className="text-sm text-earth-400 mb-5">One-time enrollment</p>

            <ul className="space-y-2 text-sm text-earth-600 mb-6">
              {[
                [`${project.duration_hours}-hour guided project`, true],
                ['5 tasks with step-by-step instructions', true],
                ['Per-task MCQ quiz verification', true],
                ['Auto-generated completion certificate', true],
                ['Project documentation download', true],
                ['Lifetime access', true],
              ].map(([text, ok]) => (
                <li key={text} className={`flex items-center gap-2 ${ok ? '' : 'line-through text-earth-300'}`}>
                  <CheckCircle className={`w-4 h-4 flex-shrink-0 ${ok ? 'text-green-500' : 'text-earth-300'}`} />
                  {text}
                </li>
              ))}
            </ul>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            {enrolled ? (
              <Link
                to={`/internships/${id}/workspace`}
                className="block w-full text-center bg-saffron-600 hover:bg-saffron-700 text-white font-bold py-3 rounded-xl transition-colors"
              >
                <Unlock className="inline w-4 h-4 mr-1 -mt-0.5" /> Go to Workspace
              </Link>
            ) : (
              <button
                onClick={handleEnroll}
                disabled={paying}
                className="w-full bg-saffron-600 hover:bg-saffron-700 disabled:bg-saffron-200 text-white font-bold py-3 rounded-xl transition-colors"
              >
                {paying ? 'Opening Payment…' : (
                  <><Lock className="inline w-4 h-4 mr-1 -mt-0.5" /> Enroll Now</>
                )}
              </button>
            )}

            {!isAuthenticated && (
              <p className="text-center text-xs text-earth-400 mt-3">
                <Link to="/login" className="text-saffron-600 hover:underline">Login</Link> required to enroll
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
